import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useIntakeFormContext } from '../../hooks/use-form-context.hooks';
import {
  CareerPlanDirection,
  CareerPlan as CareerPlanValue,
  IntakeFormCareerPlanDTO,
} from '../../redux/slice/form-intake';
import { dispatchUpdateIntakeFormCareerPlan } from '../../redux/slice/form-intake/form-intake.operations';
import { dispatchUpsertPreEmploymentAssessment } from '../../redux/slice/pre-employment-assessment';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface CareerPlanProps {
  step: number;
  onBack: () => void;
}

interface FormData {
  careerPlan: string;
  careerPlanDirection: string;
}

const career_options = [
  {
    value: CareerPlanValue.FIND_EMPLOYMENT,
    label: 'Find employment',
  },
  {
    value: CareerPlanValue.CHANGE_JOB,
    label: 'Change job',
  },
  {
    value: CareerPlanValue.COLLEGE,
    label: 'Go to college',
  },
  {
    value: CareerPlanValue.UNIVERSITY,
    label: 'Go to university',
  },
  {
    value: CareerPlanValue.INTERNSHIP,
    label: 'Start an internship',
  },
  {
    value: CareerPlanValue.APPRENTICESHIP,
    label: 'Start an apprenticeship',
  },
  {
    value: CareerPlanValue.NOT_SURE,
    label: 'Not Sure',
  },
];
export const CareerPlan: React.FC<CareerPlanProps> = ({ step, onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { careerPlan, careerPlanDirection } = useIntakeFormContext();

  const [selectedCareerPlan, setSelectedCareerPlan] = useState(careerPlan || '');
  const [selectedCareerPlanDirection, setSelectedCareerPlanDirection] = useState(
    careerPlanDirection || '',
  );
  const [error, setError] = useState({
    plan: '',
    work_knowledge: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0); // Reset scroll to the top
  }, []);

  useEffect(() => {
    if (careerPlan) {
      setSelectedCareerPlan(careerPlan);
    }
    if (careerPlanDirection) {
      setSelectedCareerPlanDirection(careerPlanDirection);
    }
  }, [careerPlan, careerPlanDirection]);

  const handlePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCareerPlan(event.target.value);
    setError({ ...error, plan: '' });
  };

  const handleWorkKnowledgeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCareerPlanDirection(event.target.value);
    setError({ ...error, work_knowledge: '' });
  };
  const { handleSubmit } = useForm<FormData>();
  const submitCareerPlan: SubmitHandler<FormData> = async () => {
    logger.debug('Submitted career plan form.', {
      selectedCareerPlan,
      selectedCareerPlanDirection,
    });

    if (!selectedCareerPlan) {
      setError({ ...error, plan: 'Please select an option' });
    } else if (!selectedCareerPlanDirection.length) {
      setError({ ...error, work_knowledge: 'Please select an option.' });
    } else {
      const dto: IntakeFormCareerPlanDTO = {
        careerPlan: selectedCareerPlan as CareerPlanValue,
        careerPlanDirection: selectedCareerPlanDirection as CareerPlanDirection,
      };
      await dispatch(dispatchUpdateIntakeFormCareerPlan(dto));
      await dispatch(
        dispatchUpsertPreEmploymentAssessment({
          requiresCareerReadinessAssessment: dto.careerPlanDirection !== CareerPlanDirection.NO,
          requiresEmploymentReadiness: dto.careerPlanDirection !== CareerPlanDirection.NO,
        }),
      );
      navigate(`/pre-employment/intake/step${step + 1}`);
    }
  };
  const backButtonClick = () => {
    onBack();
  };
  return (
    <div>
      <p className="text-lg mt-7 md:px-10">
        Do you have a clear career path in mind and well-defined goals? We're here to guide you
        through understanding your aspirations and crafting a roadmap to your dream career.
      </p>
      <div className="flex items-center mt-9 mb-10  md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">07</div>
        <div>
          <h4 className="text-2xl">
            What is your
            <span className="font-semibold"> career plan?</span>
          </h4>
        </div>
      </div>
      <form
        className="md:px-10"
        action=""
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitCareerPlan)}
      >
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          {career_options.map((option) => (
            <div key={option.value} className="age-box h-[60px]">
              <label className="radio-container h-full p-4 block relative">
                <input
                  checked={selectedCareerPlan === option.value}
                  onChange={handlePlanChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  value={option.value}
                  type="radio"
                  name="cr"
                />
                <span className="checkmark">{option.label}</span>
              </label>
            </div>
          ))}
        </div>
        {error.plan && <p className="text-red-500">{error.plan}</p>}
        <div className="flex items-center mt-9 mb-10">
          <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">08</div>
          <div>
            <h4 className="text-2xl">
              Do you know
              <span className="font-semibold"> what kind of work </span>
              you want to do?
            </h4>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <div className="age-box h-[60px]">
            <label className="radio-container h-full p-4 block relative">
              <input
                checked={selectedCareerPlanDirection === CareerPlanDirection.YES}
                onChange={handleWorkKnowledgeChange}
                className="w-full h-full absolute top-0 left-0 opacity-0"
                value={CareerPlanDirection.YES}
                type="radio"
                name="wp"
              />
              <span className="checkmark">Yes</span>
            </label>
          </div>
          <div className="radio-container  h-[60px]">
            <label className="age-wrap h-full p-4 block relative">
              <input
                checked={selectedCareerPlanDirection === CareerPlanDirection.SOMEWHAT}
                onChange={handleWorkKnowledgeChange}
                className="w-full h-full absolute top-0 left-0 opacity-0"
                value={CareerPlanDirection.SOMEWHAT}
                type="radio"
                name="wp"
              />
              <span className="checkmark">Somewhat</span>
            </label>
          </div>
          <div className="radio-container  h-[60px]">
            <label className="age-wrap h-full p-4 block relative">
              <input
                checked={selectedCareerPlanDirection === CareerPlanDirection.NO}
                onChange={handleWorkKnowledgeChange}
                className="w-full h-full absolute top-0 left-0 opacity-0"
                value={CareerPlanDirection.NO}
                type="radio"
                name="wp"
              />
              <span className="checkmark">No</span>
            </label>
          </div>
        </div>
        {error.work_knowledge && <p className="text-red-500">{error.work_knowledge}</p>}
        <div className="flex justify-between mt-8">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
